<script setup lang="ts">
import Icon from './Icon.vue'
import type { Ref } from 'vue'
const operation = inject<{pending:Ref<boolean>} | null>('studio-dialog-status', null)
/**
 * The button. sm = 32px page-header control; lg = 40px form / landing call to action;
 * xs = 28px framed action inside a table row (Cancel · View · Logs).
 * gradient = the one constructive action on a page; outline = everything else.
 * Dialog footers carry no fill and no gradient (founder 2026-09-10): primary = the one action
 * that writes (green ink and hairline), danger = the irreversible one (red ink and hairline);
 * the way out stays outline and the expected action is always the rightmost button.
 * Icon sits left, optically centred; a trailing arrow goes through #trail.
 * variant icon with no #icon slot = the ⋯ more-actions button.
 */
withDefaults(defineProps<{
  label?: string
  variant?: 'outline' | 'gradient' | 'icon' | 'primary' | 'danger'
  size?: 'xs' | 'sm' | 'lg'
  type?: 'button' | 'submit'
  disabled?: boolean
  block?: boolean
}>(), { variant: 'outline', size: 'sm', type: 'button', label: undefined })
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || operation?.pending.value"
    class="relative inline-flex select-none items-center justify-center whitespace-nowrap font-sans font-normal leading-none tracking-normal antialiased transition-[background-color,box-shadow,transform] duration-150 disabled:pointer-events-none disabled:opacity-40 active:translate-y-px [&_svg]:shrink-0 [&_span]:font-normal"
    :class="[
      size === 'lg'
        ? 'h-[var(--control-height-large)] gap-2 rounded-[var(--radius-form)] px-5 text-[length:var(--type-control)] [&_svg]:size-[var(--measure-14px)]'
        : size === 'xs'
          ? 'h-[var(--control-height-small)] gap-1.5 rounded-[var(--radius-compact)] px-2.5 text-[length:var(--type-meta)] [&_svg]:size-[var(--measure-11px)]'
          : 'h-[var(--control-height)] gap-2 rounded-[var(--radius-control)] px-3.5 text-xs [&_svg]:size-3',
      block && 'w-full',
      variant === 'gradient'
        ? 'bg-gradient-primary text-primary-foreground/90 shadow-glow-cta hover:brightness-105'
        : variant === 'primary'
          ? 'border border-primary/50 bg-transparent text-primary hover:bg-primary/10'
          : variant === 'danger'
            ? 'border border-destructive/50 bg-transparent text-destructive hover:bg-destructive/10'
            : variant === 'icon'
              ? (size === 'lg' ? 'w-10' : size === 'xs' ? 'w-7' : 'w-8') + ' border border-border px-0 text-muted-foreground hover:bg-muted'
              : 'border border-border bg-transparent text-foreground hover:bg-muted',
    ]"
  >
    <span v-if="$slots.icon" class="inline-flex" :class="[label && '-ml-0.5', variant === 'gradient' && 'opacity-90']"><slot name="icon" /></span>
    <Icon v-else-if="variant === 'icon'" name="kebab" />
    <span v-if="label">{{ label }}</span>
    <span v-if="$slots.trail" class="-mr-0.5 inline-flex" :class="variant === 'gradient' && 'opacity-90'"><slot name="trail" /></span>
  </button>
</template>
